import './PricingSection.css';

import { useEffect, useState } from 'react';

const PricingSection = ({ onBack, isAuthenticated, onLogin }) => {
  const [billingCycle, setBillingCycle] = useState('monthly'); // Default to monthly
  const [isLoading, setIsLoading] = useState(false);
  const [userPricing, setUserPricing] = useState(null);
  const [fetchingPricing, setFetchingPricing] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  // Fetch user pricing information when authenticated
  useEffect(() => {
    const fetchUserPricing = async () => {
      if (!isAuthenticated) return;

      setFetchingPricing(true);
      try {
        const response = await fetch('/api/pricing/user', {
          credentials: 'include',
        });

        if (response.ok) {
          const pricingData = await response.json();
          setUserPricing(pricingData);
        } else {
          console.error('Failed to fetch user pricing:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching user pricing:', error);
      } finally {
        setFetchingPricing(false);
      }
    };

    fetchUserPricing();
  }, [isAuthenticated]);

  const handlePlanSelect = async plan => {
    if (!isAuthenticated) {
      onLogin();
      return;
    }

    if (plan === 'enterprise') {
      // Handle enterprise plan differently (e.g., contact form)
      console.log('Contact sales for enterprise plan');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/pricing/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan: plan,
          interval: billingCycle,
        }),
      });

      const data = await response.json();

      if (response.ok && data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        console.error('Failed to create checkout session:', data.error);
        alert('Failed to create checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const plans = {
    monthly: {
      pro: { price: '$4.99', period: 'per month', yearlyPrice: '$39.99/year' },
      premium: { price: '$9.99', period: 'per month', yearlyPrice: '$79.99/year' },
      enterprise: { price: 'Custom', period: '', yearlyPrice: '' },
    },
    yearly: {
      pro: { price: '$39', period: 'per year', yearlyPrice: 'Save $20.88 (4+ months free)' },
      premium: { price: '$79.99', period: 'per year', yearlyPrice: 'Save $39.89 (4+ months free)' },
      enterprise: { price: 'Custom', period: '', yearlyPrice: '' },
    },
  };

  const currentPlans = plans[billingCycle];

  // Helper function to check if user has current plan
  const isCurrentPlan = plan => {
    if (!userPricing) return false;
    return userPricing.pricing_plan === plan;
  };

  // Helper function to get button text
  const getButtonText = plan => {
    if (!isAuthenticated) {
      return `Sign up for ${plan.charAt(0).toUpperCase() + plan.slice(1)}`;
    }

    if (fetchingPricing) {
      return 'Loading...';
    }

    if (isCurrentPlan(plan)) {
      return 'Current Plan';
    }

    if (isLoading) {
      return 'Loading...';
    }

    return `Choose ${plan.charAt(0).toUpperCase() + plan.slice(1)}`;
  };

  const toggleFaq = index => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const faqData = [
    {
      question: 'How does the menu processing work?',
      answer:
        'Simply upload a photo of your menu, and our AI will extract the text, generate high-quality images for each dish, and create a beautiful digital menu with allergen information and nutritional details.',
    },
    {
      question: 'How can I cancel my subscription?',
      answer: (
        <span>
          Send an email to{' '}
          <a
            href="mailto:jordiboschbosch@hotmail.com"
            style={{ color: '#3b82f6', textDecoration: 'underline', fontWeight: '600' }}
          >
            jordiboschbosch@hotmail.com
          </a>{' '}
          with your stripe purchasing email. We will build the self-serve cancellation form ASAP.
        </span>
      ),
    },
    {
      question: 'What image formats are supported?',
      answer:
        'We support all common image formats including JPG, PNG and WebP. Be aware that sometimes "live" photos can cause problems (since they are a video under the hood).',
    },
    {
      question: 'Is there a free trial available?',
      answer:
        'Yes, you can try our service with up to 5 menu uploads for free + 1 weekly. No credit card required to get started.',
    },
  ];

  return (
    <div className="pricing-section">
      <div className="pricing-header">
        <div className="pricing-hero">
          <h1 className="pricing-title">Plans & Pricing</h1>
        </div>
      </div>

      <div className="billing-toggle">
        <button
          className={`interval-button ${billingCycle === 'monthly' ? 'active' : ''}`}
          onClick={() => setBillingCycle('monthly')}
        >
          Monthly
        </button>
        <button
          className={`interval-button yearly ${billingCycle === 'yearly' ? 'active' : ''}`}
          onClick={() => setBillingCycle('yearly')}
        >
          🔥 Yearly: get 4+ months free
        </button>
      </div>

      <div className="plans-container">
        <div className={`plan-card ${isCurrentPlan('pro') ? 'current-plan' : ''}`}>
          <div className="plan-header">
            <h3 className="plan-name">Pro</h3>
            <div className="plan-price">
              <span className="price">{currentPlans.pro.price}</span>
              <span className="period">{currentPlans.pro.period}</span>
            </div>
            {billingCycle === 'monthly' && <p className="plan-yearly">or $39.99/year</p>}
            {billingCycle === 'yearly' && <p className="plan-yearly savings">{currentPlans.pro.yearlyPrice}</p>}
          </div>

          <div className="plan-features">
            <ul className="features-list">
              <li>100 scans per month</li>
              <li>Improved food models 🌮</li>
              <li>🔥 Calories included</li>
              <li>See all allergens information</li>
              <li>Detailed nutritional breakdown</li>
              <li>See all ingredients</li>
              <li>Dietary restriction filters</li>
            </ul>

            {billingCycle === 'monthly' && (
              <div className="yearly-savings">
                <button className="savings-link" onClick={() => setBillingCycle('yearly')}>
                  Save with yearly (4+ months free) ↗
                </button>
              </div>
            )}
          </div>

          <button
            className={`plan-button primary ${isCurrentPlan('pro') ? 'current' : ''}`}
            onClick={() => handlePlanSelect('pro')}
            disabled={isLoading || fetchingPricing || isCurrentPlan('pro')}
          >
            {getButtonText('pro')}
          </button>
        </div>

        <div className={`plan-card popular ${isCurrentPlan('premium') ? 'current-plan' : ''}`}>
          <div className="popular-badge">Most popular</div>
          <div className="plan-header">
            <h3 className="plan-name">Premium</h3>
            <div className="plan-price">
              <span className="price">{currentPlans.premium.price}</span>
              <span className="period">{currentPlans.premium.period}</span>
            </div>
            {billingCycle === 'monthly' && <p className="plan-yearly">or $79.99/year</p>}
            {billingCycle === 'yearly' && <p className="plan-yearly savings">{currentPlans.premium.yearlyPrice}</p>}
          </div>

          <div className="plan-features">
            <ul className="features-list">
              <li>Unlimited scans for the time period</li>
              <li>State of the art food models 🌮 (AI)</li>
              <li>🔥 Calories included</li>
              <li>See all allergens information</li>
              <li>See all ingredients</li>
              <li>Dietary restriction filters</li>
              <li>All Plus features included</li>
              <li>Faster response time, lower latency</li>
            </ul>

            {billingCycle === 'monthly' && (
              <div className="yearly-savings">
                <button className="savings-link" onClick={() => setBillingCycle('yearly')}>
                  Save with yearly (4+ months free) ↗
                </button>
              </div>
            )}
          </div>

          <button
            className={`plan-button primary featured ${isCurrentPlan('premium') ? 'current' : ''}`}
            onClick={() => handlePlanSelect('premium')}
            disabled={isLoading || fetchingPricing || isCurrentPlan('premium')}
          >
            {getButtonText('premium')}
          </button>
        </div>

        <div className="plan-card">
          <div className="plan-header">
            <h3 className="plan-name">Enterprise</h3>
            <p className="plan-subtitle">"For restaurants"</p>
            {currentPlans.enterprise.price !== 'Custom' && (
              <div className="plan-price">
                <span className="price">{currentPlans.enterprise.price}</span>
                <span className="period">{currentPlans.enterprise.period}</span>
              </div>
            )}
            {currentPlans.enterprise.price === 'Custom' && (
              <div className="plan-price">
                <span className="price">Custom plan</span>
              </div>
            )}
          </div>

          <div className="plan-features">
            <ul className="features-list">
              <li>Automatically build and customize your menu with highly resolution images</li>
              <li>Increase average ticket </li>
              <li>QR code integration</li>
              <li>Customize your menu descriptions, images, and more</li>
              <li>Automate allergen reports</li>
              <li>Automatically display nutritional information</li>
              <li>Multi-language support</li>
              <li>High resolution images</li>
              <li>Near 0 latency</li>
            </ul>
          </div>

          <button className="plan-button primary" onClick={() => handlePlanSelect('enterprise')} disabled={isLoading}>
            {isLoading ? 'Loading...' : isAuthenticated ? 'Contact us' : 'Sign up for Enterprise'}
          </button>
        </div>
      </div>

      <div className="faq-section">
        <div className="faq-header">
          <h2>Frequently Asked Questions</h2>
          <p className="faq-subtitle">Everything you need to know about our menu digitization service</p>
        </div>

        <div className="faq-list">
          {faqData.map((faq, index) => (
            <div key={index} className={`faq-item ${openFaqIndex === index ? 'active' : ''}`}>
              <button className="faq-question" onClick={() => toggleFaq(index)} aria-expanded={openFaqIndex === index}>
                <span className="question-text">{faq.question}</span>
                <span className={`faq-toggle ${openFaqIndex === index ? 'rotated' : ''}`}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              </button>

              <div className={`faq-answer ${openFaqIndex === index ? 'expanded' : ''}`}>
                <div className="answer-content">
                  <p>{faq.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PricingSection;
